import {useVirtualizer2D, type VirtualizerAxisView} from '@github-ui/virtualizer'
import {type RefCallback, type RefObject, useCallback, useLayoutEffect, useMemo} from 'react'

import {type BoardAxisMetrics, useBoardMetrics} from './use-board-metrics'
import {useFocusSafetyNet} from './use-focus-safety-net'

/**
 * How many items either side of the viewport stay rendered.
 *
 * One, on both axes. An item here is a whole column or a whole swimlane rather than a
 * card, so each one costs a strip of cells, and the board's items are a uniform size,
 * which means the window's edges land exactly where the arithmetic says rather than
 * somewhere an estimate settled.
 */
const OVERSCAN = 1

/**
 * How much of an axis the board draws before it has measured anything.
 *
 * A window needs one layout pass to exist, and what the board draws in that pass is the
 * whole cost this change removes. A board of two hundred columns by twenty-four swimlanes
 * that drew itself once in full would build four thousand eight hundred cells, and each
 * cell mounts a resize observer, forces a layout and walks its own subtree to work out
 * whether a keyboard needs a way into it. All of it thrown away one render later, and long
 * enough on the main thread for a browser to call the page unresponsive.
 *
 * So an axis nobody has measured yet draws this many items and stops. Eight columns is
 * over one hundred and seventy rem of them, because a column is at least
 * `--board-column-inline-size` wide and that is `22rem`, so no viewport shows eight at
 * once. Eight rows is the same statement about depth, from the other direction: a board
 * that has enough swimlanes for this to bite is one that states a band to put them in, and
 * eight bands is deeper than a viewport at any depth worth stating. It is a bound rather
 * than a measurement, and a reader never sees it, because the measurement happens in a
 * layout effect and the real window replaces this before anything is painted. What it is
 * for is the render nobody can correct: the one on a server, where there is nothing to
 * measure against at all.
 */
const UNMEASURED_ITEMS = 8

/** Which items an axis renders, and what stands in for the rest. */
export type BoardAxisWindow = {
  /** The indexes to render, in order. */
  indexes: readonly number[]
  /**
   * Extent to reserve before the first rendered item, or `null` when there is nothing
   * before it.
   *
   * The reservation is a box in the ordinary flow rather than an offset on the items,
   * which is what keeps everything around it true: a row still resolves its height
   * against the band it is laid out in rather than against a container as tall as every
   * row, a row header still sticks to the same containing block, and each axis's paging
   * ghost still sits after the last item it is a sibling of.
   */
  leadingSize: number | null
  /** The same at the far end, which is what makes the scrollbars describe the whole board. */
  trailingSize: number | null
  /**
   * Move this axis until the item at an index is in view.
   *
   * For the one case a scroll offset cannot answer: focus owed to a position whose column
   * or row this window left out. There is no element to focus and none to scroll to, so
   * the window has to be moved to the index before anything else can happen. A no-op on an
   * axis that is not windowing, which is an axis where every item is already drawn.
   */
  scrollToIndex: VirtualizerAxisView<HTMLElement, HTMLElement>['scrollToIndex']
}

export type BoardWindowResult = {
  columns: BoardAxisWindow
  rows: BoardAxisWindow
  columnHeadersRef: RefCallback<HTMLElement>
  rowsRef: RefCallback<HTMLElement>
  columnProbeRef: RefCallback<HTMLElement>
  rowProbeRef: RefCallback<HTMLElement>
}

export type UseBoardWindowOptions = {
  /** The board's own scroll container. Both windows measure against this one element. */
  scrollRef: RefObject<HTMLElement | null>
  columnCount: number
  rowCount: number
  /**
   * The board has grown past the window, so the page is scrolling instead of it.
   *
   * Neither axis windows while this is true. A window is a statement about a scroll
   * viewport, and a container that never scrolls has none: what it reports is the size of
   * everything in it, so the offsets a window would work from never move and the parts it
   * left out could not be reached by scrolling anything. Rendering the whole board is both
   * what it did before and the only thing a reader can still get to, and the console
   * report `@github-ui/board` makes from this is what says the page is wrong.
   */
  hasUnboundedHeight: boolean
}

/**
 * Two virtual windows over the board's one scroll container: one across the columns and
 * one down the rows.
 *
 * The column window is used twice, in the sticky header strip and again inside every
 * rendered row, and it is the same window both times rather than two agreeing ones,
 * because a header sitting over the wrong cell is what disagreement looks like.
 *
 * Neither axis measures an element. Both extents are stated once for the whole board — a
 * column's width and a row's height — so `useBoardMetrics` resolves each to a number and
 * every item on an axis is that number. That is also what keeps the three constraints
 * `useVirtualizer2D` names off the board: nothing is measured, so no element needs a
 * per-axis `indexAttribute`; nothing is positioned by the virtualizer, so
 * `directDomUpdates` never overwrites one axis' offset with the other's; and no sizing
 * container is shared, so there are no `containerRef`s to merge.
 *
 * An axis windows only once it has more extent than the box it sits in, and only while the
 * board has a height to window against. Below that every item is on screen anyway, so a
 * window would leave nothing out. See {@link UseBoardWindowOptions.hasUnboundedHeight} for
 * the other half.
 */
export function useBoardWindow({
  scrollRef,
  columnCount,
  rowCount,
  hasUnboundedHeight,
}: UseBoardWindowOptions): BoardWindowResult {
  const {metrics, columnHeadersRef, rowsRef, columnProbeRef, rowProbeRef} = useBoardMetrics({
    scrollRef,
    columnCount,
    rowCount,
  })

  // A windowed item that leaves the page takes any focus inside it with it.
  useFocusSafetyNet(scrollRef)

  const columns = metrics?.columns ?? UNMEASURED
  const rows = metrics?.rows ?? UNMEASURED
  const isRtl = metrics?.isRtl ?? false

  const windowsColumns = !hasUnboundedHeight && canWindow(columns, columnCount)
  const windowsRows = !hasUnboundedHeight && canWindow(rows, rowCount)

  const getScrollElement = useCallback(() => scrollRef.current, [scrollRef])
  const estimateColumn = useCallback(() => columns.itemSize, [columns.itemSize])
  const estimateRow = useCallback(() => rows.itemSize, [rows.itemSize])

  const view = useVirtualizer2D<HTMLElement, HTMLElement, HTMLElement>({
    getScrollElement,
    horizontal: {
      count: columnCount,
      enabled: windowsColumns,
      estimateSize: estimateColumn,
      gap: columns.gap,
      scrollMargin: columns.start,
      overscan: OVERSCAN,
      isRtl,
    },
    vertical: {
      count: rowCount,
      enabled: windowsRows,
      estimateSize: estimateRow,
      gap: rows.gap,
      scrollMargin: rows.start,
      overscan: OVERSCAN,
    },
  })

  const measured = metrics !== null
  const columnFallback = useFallback(columnCount, measured && !windowsColumns)
  const rowFallback = useFallback(rowCount, measured && !windowsRows)

  useResolvedSize(view.horizontal.measure, columns.itemSize)
  useResolvedSize(view.vertical.measure, rows.itemSize)

  return {
    columns: toWindow(view.horizontal, columnFallback, columns.gap, windowsColumns),
    rows: toWindow(view.vertical, rowFallback, rows.gap, windowsRows),
    columnHeadersRef,
    rowsRef,
    columnProbeRef,
    rowProbeRef,
  }
}

/** What an axis looks like before the board has been laid out, and on the server. */
const UNMEASURED: BoardAxisMetrics = {itemSize: 0, gap: 0, start: 0, viewport: 0}

/**
 * Whether this axis needs more room than the board gives it.
 *
 * Worked out from the item size and the count rather than read off the rendered board, so
 * the answer is the same before the board has drawn anything as after. Reading it off the
 * DOM would mean drawing every column and row once to find out whether to draw every
 * column and row, which is both the cost this exists to remove and a decision that could
 * flip back the moment it took effect.
 */
function canWindow(axis: BoardAxisMetrics, count: number): boolean {
  return axis.itemSize > 0 && count * (axis.itemSize + axis.gap) - axis.gap > axis.viewport
}

/**
 * Hands a re-resolved item size to a virtualizer that would otherwise never ask for it.
 *
 * `estimateSize` is the only channel a size travels down, and the virtualizer memoises its
 * measurements on the count, the gap, the scroll margin and its own cache version — never
 * on `estimateSize` itself. Nothing here measures an element, so that cache version never
 * moves on its own, and a size that changes while the count and the gap stay put would be
 * computed, re-rendered and then ignored.
 *
 * It is not a rare shape. A caller changing `rowHeight`, a board resized while its rows are
 * the `100%` default, and a viewport narrowed past `24rem` so a column starts tracking it
 * all move a size and nothing else. The last one is the one that bites: the window's pitch
 * would drift from the layout's a little further with every column, which is the header
 * standing over the wrong cell that the single shared column window exists to prevent.
 *
 * Before paint, because the alternative is a frame drawn at the old pitch.
 */
function useResolvedSize(measure: () => void, itemSize: number): void {
  useLayoutEffect(() => {
    measure()
  }, [measure, itemSize])
}

/**
 * What an axis draws when it has no window to draw from.
 *
 * Every item only once the board has measured and decided this axis fits, which is the
 * one case where every item really is on screen. Otherwise a screenful: before the first
 * measurement, and in the moment an axis has decided to window but its virtualizer has
 * not answered yet. Neither of those is a reason to build the whole board.
 */
function useFallback(count: number, drawEveryItem: boolean): readonly number[] {
  const drawn = drawEveryItem ? count : Math.min(count, UNMEASURED_ITEMS)

  return useMemo(() => Array.from({length: drawn}, (_, index) => index), [drawn])
}

/**
 * The window an axis renders this frame.
 *
 * An axis with no window falls back to what {@link useFallback} decided, which is every
 * item when the axis fits and a screenful otherwise. An enabled axis over a non-empty
 * board always has at least one item, so an empty set means its virtualizer has not
 * answered rather than that there is nothing to show.
 */
function toWindow(
  axis: VirtualizerAxisView<HTMLElement, HTMLElement>,
  fallback: readonly number[],
  gap: number,
  windowing: boolean,
): BoardAxisWindow {
  const items = axis.items
  const first = items[0]
  const last = items[items.length - 1]

  if (!windowing || first === undefined || last === undefined) {
    return {indexes: fallback, leadingSize: null, trailingSize: null, scrollToIndex: axis.scrollToIndex}
  }

  // Both reservations are expressed in the container's own coordinates, and the
  // virtualizer answers in two: an item's `start` is measured from the scroll container,
  // so it carries `scrollMargin`, while `totalSize` has already had it taken off. The
  // spacers sit inside a container that begins at `scrollMargin`, so the margin comes off
  // the item offsets before either reservation is worked out. Mixing the two leaves a
  // permanent blank band at the near end of the axis and a scroll extent that changes by
  // the same amount as the window moves, which is the one thing reserving is for.
  //
  // Each reservation then gives up one gap, because the gap between it and the item beside
  // it is drawn by the container, exactly as it is between two items. A reservation at
  // either end therefore works out negative when nothing was left out, which is the same
  // statement as rendering none.
  const leading = first.start - axis.scrollMargin
  const trailing = axis.totalSize - (last.end - axis.scrollMargin)

  return {
    indexes: items.map(item => item.index),
    leadingSize: reserved(leading - gap),
    trailingSize: reserved(trailing - gap),
    scrollToIndex: axis.scrollToIndex,
  }
}

function reserved(size: number): number | null {
  return size > 0 ? size : null
}
